import { GQL } from "@binale-tech/shared";
import { type CalendarEvent, type EventCategory } from "./types";

// SP2 (FIBU-22). One Bescheid amount (quarterlyAmountCents) resolves onto ALL FOUR
// derived VZ events for a year via an effective-year lookup - not a foreign key to any
// single synthetic id (design spec §II.6/§II.12.3). Extracted as a pure module so it's
// independently testable, unlike the inline UStVA-amount-attachment loop in
// useTaxDeadlineEvents.ts.
export const effectivePrepaymentCents = (
    rows: readonly GQL.ITaxPrepayment[],
    taxType: GQL.ITaxPrepaymentType,
    year: number,
): number | undefined => {
    const candidates = rows.filter((r) => r.taxType === taxType && r.validFromYear <= year);
    if (!candidates.length) {
        return undefined;
    }
    return candidates.toSorted((a, b) => b.validFromYear - a.validFromYear)[0].quarterlyAmountCents;
};

const CATEGORY_TO_TAX_TYPE: Partial<Record<EventCategory, GQL.ITaxPrepaymentType>> = {
    "vz-est": GQL.ITaxPrepaymentType.Est,
    "vz-kst": GQL.ITaxPrepaymentType.Kst,
    "vz-gewst": GQL.ITaxPrepaymentType.Gewst,
};

// Only touches vz-est/vz-kst/vz-gewst events - ustva/sondervz/annual/offenlegung/user
// pass through unchanged (ustva's amount is attached separately from useVatPosition;
// sondervz has no amount in v1, §II.11 decision 5).
export const attachPrepaymentAmounts = (
    events: readonly CalendarEvent[],
    rows: readonly GQL.ITaxPrepayment[],
    year: number,
): CalendarEvent[] =>
    events.map((e) => {
        const taxType = CATEGORY_TO_TAX_TYPE[e.category];
        if (!taxType) {
            return e;
        }
        const amountCents = effectivePrepaymentCents(rows, taxType, year);
        return amountCents === undefined ? e : { ...e, amountCents };
    });
